/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * WorkflowDemoChrome — page-level demo-mode side effects.
 *
 * Mounted once per Workflow page. When demo mode is on, this component:
 *   1. Adds a `wf-demo-active` class to document.body so global CSS can
 *      bump font sizes / hide debug chrome without each child needing to
 *      know about demo mode.
 *   2. Renders a fixed-corner watermark so anyone watching a recording
 *      knows what they're seeing.
 *
 * It renders nothing when demo mode is off and cleans up the body class
 * on unmount, so navigating away from a workflow page leaves the body
 * in its baseline state.
 */

import React, { useEffect, useState } from 'react';
import { isDemoMode, subscribeDemoMode } from '../../utils/demoMode';

export default function WorkflowDemoChrome({ label = 'SkillDelivery — Workflow Demo' }) {
  const [enabled, setEnabled] = useState(isDemoMode());

  useEffect(() => subscribeDemoMode(setEnabled), []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const body = document.body;
    if (enabled) body.classList.add('wf-demo-active');
    else body.classList.remove('wf-demo-active');
    return () => body.classList.remove('wf-demo-active');
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="wf-demo-watermark" aria-hidden="true">
      <span className="wf-demo-watermark-mark">●</span>
      <span className="wf-demo-watermark-label">{label}</span>
    </div>
  );
}
